// src/components/CompanyPage/CompanyPageContext.tsx 수정
import { createContext, useContext, ReactNode, useState, useEffect } from 'react'

import CompanyPageCacheMonitor from './CompanyPageCacheMonitor'

import {
  COMPANY_PAGE_CACHE_TRACKING_KEY,
  CompanyPageCachingTrackingItem,
} from '@/constants/company_page'

interface CompanyPageCachingContextType {
  cachingItems: CompanyPageCachingTrackingItem[]
  isCaching: (conversationId: string) => boolean
}

const CompanyPageCachingContext = createContext<CompanyPageCachingContextType | undefined>(
  undefined
)

export const CompanyPageCachingProvider = ({ children }: { children: ReactNode }) => {
  const [cachingItems, setCachingItems] = useState<CompanyPageCachingTrackingItem[]>([])

  useEffect(() => {
    const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
    if (stored) {
      try {
        const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
        setCachingItems(items)
      } catch (error) {
        console.error('로컬스토리지 파싱 에러:', error)
        localStorage.removeItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
      }
    }

    const checkLocalStorage = () => {
      const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
      if (stored) {
        const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
        setCachingItems(items)
      } else {
        setCachingItems([])
      }
    }

    const interval = setInterval(checkLocalStorage, 3000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === COMPANY_PAGE_CACHE_TRACKING_KEY) {
        if (e.newValue) {
          try {
            const items: CompanyPageCachingTrackingItem[] = JSON.parse(e.newValue)
            setCachingItems(items)
          } catch (error) {
            console.error('Storage event 파싱 에러:', error)
          }
        } else {
          setCachingItems([])
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const isCaching = (conversationId: string) => {
    return cachingItems.some((item) => item.conversationId === conversationId)
  }

  return (
    <CompanyPageCachingContext.Provider value={{ cachingItems, isCaching }}>
      {cachingItems.map((item) => (
        <CompanyPageCacheMonitor key={item.messageId} item={item} />
      ))}
      {children}
    </CompanyPageCachingContext.Provider>
  )
}

export const useCompanyPageCaching = () => {
  const context = useContext(CompanyPageCachingContext)
  if (context === undefined) {
    throw new Error('useCompanyPageCaching must be used within CompanyPageCachingProvider')
  }
  return context
}
